import _ from 'lodash'
import axios, { AxiosRequestHeaders, AxiosResponse } from 'axios'

import { IhmLoginTokenProvider } from '@api/utils/IhmLoginTokenProvider'
import {
    getParameterByName,
    EnvironmentDetail,
    Exception,
    UUIDUtils,
    LatencyMetric,
    CountMetric,
} from '@amzn/ihm-associate-tools-js-lib'
import { store } from '@atredux/store'
import {
    getStoreInfo,
    isConfigured,
} from '@atredux/selectors/storeSelector'
import {
    IhmLoginTokenRetrievalStateMismatchException,
    IhmLoginTokenRetrievalUnknownErrorException,
    IhmLoginTokenRetrievalClientErrorException,
    IhmLoginTokenRetrievalNetworkingException,
    IhmLoginTokenRetrievalInternalServerErrorException,
} from '@utils/Exceptions/IhmLoginExceptions'
import * as FeatureConfigService from '@services/FeatureConfigService'
import { UserTypeService } from '@pages/UserTypeSelection/utils/UserTypeService'
import { StoreIdService } from '@pages/StoreIdSelection/utils/StoreIdService'
import { DorothyUtils } from '@utils/DorothyUtils'

enum IhmLoginServiceClientConstants {
    IHM_LOGIN_REDIRECT_URI_LOCAL_STORAGE_KEY = 'ihmLoginRedirectUri',
    IHM_LOGIN_STATE_LOCAL_STORAGE_KEY = 'ihmLoginState',
}

export class IhmLoginServiceClient {
    public static getIhmLoginVip (): string {
        // TODO: Use IhmLoginClientConfig to get these endpoints
        // Keeping this same as it was before in Old seaward
        console.info('ATBase: Getting Environment Data for IhmLogin')
        const region = (StoreIdService.getSelectedStoreIdRegion() || EnvironmentDetail.getRegion() || '').toLowerCase()
        const amazonDomain = EnvironmentDetail.getDomain()
        console.info('ATBase: Got Environment Data for IhmLogin: ', region, amazonDomain)
        switch (amazonDomain) {
        case 'prod':
            if (region === 'iad') {
                console.log('ATBase: Returning IhmLogin IAD endpoint')
                return 'https://ihmlogin-i-us-east-1.amazon.com'
            }
            if (region === 'dub') {
                console.log('ATBase: Returning IhmLogin DUB endpoint')
                return 'https://ihmlogin-d-eu-west-1.amazon.com'
            }
            if (region === 'sin') {
                console.log('ATBase: Returning IhmLogin PROD SIN endpoint')
                return 'https://ihmlogin-s-ap-southeast-1.amazon.com'
            }
            return 'https://ihmlogin-us-west-2.amazon.com'
        case 'gamma':
            if (region === 'dub') {
                return 'https://ihmlogin-d-preview-eu-west-1.amazon.com'
            }
            if (region === 'sin') {
                console.log('ATBase: Returning IhmLogin GAMMA SIN endpoint')
                return 'https://ihmlogin-s-preview-ap-southeast-1.amazon.com'
            }
            return 'https://ihmlogin-preview-us-west-2.amazon.com'
        case 'test':
            return 'https://ihmlogin-pdx.integ.amazon.com'
        default:
            return ''
        }
    }

    public static getAssociateToolsBaseUrl (): string {
        const absUrl = window.location.href

        return absUrl.split('?')[0]
    }

    public static getLocale (): string {
        return getParameterByName('locale')
    }

    public static getAuthorizeOrUserSelectionEndpoint (): string {
        const dorothyUserTypeSelection = typeof UserTypeService.getUserType() !== 'undefined' ||
            (isConfigured(store.getState()) && getStoreInfo(store.getState()).isDorothyEnabled)
        if (!dorothyUserTypeSelection && FeatureConfigService.isFeatureEnabled(
            FeatureConfigService.featureConfigFeatures.EXTERNAL_USER_LOGIN_FEATURE, false)) {
            return '/user-type-selection'
        }
        return '/authorize'
    }

    public static getUriParamsWithEnvironmentDetails (): string {
        const amazonDomain = EnvironmentDetail.getDomain()
        const region = StoreIdService.getSelectedStoreIdRegion() || EnvironmentDetail.getRegion()
        const storeId = StoreIdService.getSelectedStoreId() || EnvironmentDetail.getStoreId()
        const storeFamily = EnvironmentDetail.getStoreFamily()
        const initialTool = EnvironmentDetail.getInitialTool()
        const initialZoneInfo = EnvironmentDetail.getInitialZoneInfo()
        const suppressConnections = EnvironmentDetail.getSuppressConnections()

        let result = 'domain=' + amazonDomain + '%26region=' + region + '%26storeId=' + storeId

        if (storeFamily) {
            result += '%26storeFamily=' + encodeURIComponent(encodeURIComponent(storeFamily))
        }
        if (initialTool) {
            result += '%26initialTool=' + encodeURIComponent(encodeURIComponent(initialTool))
        }
        if (initialZoneInfo) {
            // encodeURIComponent twice because that value is used in return url that should be encoded
            if (initialZoneInfo.zoneName) {
                result += '%26initialZoneName=' + encodeURIComponent(encodeURIComponent(initialZoneInfo.zoneName))
            }
            if (initialZoneInfo.subzoneName) {
                result += '%26initialSubzoneName=' + encodeURIComponent(encodeURIComponent(initialZoneInfo.subzoneName))
            }
        }
        if (suppressConnections && suppressConnections === 'true') {
            result += '%26suppressConnections=true'
        }

        return result
    }

    /**
     * Creates a URL that can be used to sign in using the Auth Code flow. It also saves the redirect uri
     * and saves a generated state in localStorage so that they can be retrieved after successful authentication.
     */
    public static getIhmLoginSignInUrlForAuthFlow (): string {
        let redirectUri
        const associateToolsBaseUrl = IhmLoginServiceClient.getAssociateToolsBaseUrl()
        const ihmLoginVip = IhmLoginServiceClient.getIhmLoginVip()
        // TODO: Replace clientId with ATUI base tool one
        const clientId = 'IhmPewterWebsite'
        const storeId = StoreIdService.getSelectedStoreId() || EnvironmentDetail.getStoreId()
        const state = UUIDUtils.generate()
        const locale = IhmLoginServiceClient.getLocale()

        const storeFamily = EnvironmentDetail.getStoreFamily()
        const scope = IhmLoginServiceClient.generateScope(storeFamily)
        const userType = UserTypeService.getUserType()
        redirectUri = associateToolsBaseUrl + '?' + IhmLoginServiceClient.getUriParamsWithEnvironmentDetails() + '%26badgeId=1234%26auth=ihm-login'

        redirectUri += locale ? '%26locale=' + locale : ''
        redirectUri += '%23/login/'

        window.localStorage.setItem(
            IhmLoginServiceClientConstants.IHM_LOGIN_REDIRECT_URI_LOCAL_STORAGE_KEY, redirectUri)
        window.localStorage.setItem(
            IhmLoginServiceClientConstants.IHM_LOGIN_STATE_LOCAL_STORAGE_KEY, state)

        const endpointPath = IhmLoginServiceClient.getAuthorizeOrUserSelectionEndpoint()
        const url = ihmLoginVip + endpointPath + '?response_type=code&client_id=' + clientId + '&redirect_uri=' +
                    redirectUri + '&scope=' + scope + '&state=' + state + '&storeId=' + storeId + (userType ? '&userType=' + userType : '') + (locale ? '&locale=' + locale : '')
        console.log('ATBase: AuthCode URL:', url)
        return url
    }

    /**
     * Calls IhmLogin's /token endpoint to retrieve the token using the code (extracted from the URL).
     * This requires relies on retrieving the redirect url and the state from localStorage
     * (saved in getIhmLoginSignInUrlForAuthFlow function).
     *
     * @return {Promise} Returns a an object with the tokens and scopes on success and an
     * {@see IhmLoginTokenRetrievalException} on error.
     *  Successful response example:
     *      {
     *          access_token: {String}, // This is the token that is sent to the backend services.
     *          expires_in: {Number}, // Time in seconds until the token expires.
     *          id_token: {String}, // Id token for the logged in user.
     *          scope: {String}, // Space separated scopes.
     *          token_type: {String} // The token type (usually "Bearer").
     *      }
     *  It rejects with a child of {@see IhmLoginTokenRetrievalRetriableException} if the exception
     *  is retriable and a child of {@see IhmLoginTokenRetrievalNonRetriableException} when the problem
     *  is not retriable.
     */
    public static retrieveAuthToken (): Promise<any> {
        const ihmLoginVip = IhmLoginServiceClient.getIhmLoginVip()
        const code = getParameterByName('code')
        const state = getParameterByName('state')

        const redirectUri = window.localStorage.getItem(
            IhmLoginServiceClientConstants.IHM_LOGIN_REDIRECT_URI_LOCAL_STORAGE_KEY)
        const savedState = window.localStorage.getItem(
            IhmLoginServiceClientConstants.IHM_LOGIN_STATE_LOCAL_STORAGE_KEY)
        const url = ihmLoginVip + '/token?client_id=IhmPewterWebsite&grant_type=authorization_code&redirect_uri='
            + redirectUri + '&code=' + code

        if (state !== savedState) {
            console.log('ATBase: state', state, 'does not match', savedState)
            return Promise.reject(new IhmLoginTokenRetrievalStateMismatchException({
                message: `Saved state ${savedState} does not match ${state}`,
            }))
        }

        const headers: AxiosRequestHeaders = {
            'Content-Type': 'application/x-www-form-urlencoded',
        }
        console.log('ATBase: Making request to get token to', url)
        const retrieveAuthTokenLatency = new LatencyMetric(undefined, 'RetrieveAuthTokenLatency', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        const retrieveAuthTokenSuccess = new CountMetric(undefined, 'RetrieveAuthTokenSuccess', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        const retrieveAuthTokenError = new CountMetric(undefined, 'RetrieveAuthTokenFailure', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        retrieveAuthTokenLatency.start()
        return axios({
            method: 'POST',
            url,
            headers,
            withCredentials: true,
        }).then((response) => {
            console.log('ATBase: Successful call to /token endpoint')
            retrieveAuthTokenLatency.stop()
            retrieveAuthTokenSuccess.count(1)
            retrieveAuthTokenError.count(0)
            window.localStorage.removeItem(
                IhmLoginServiceClientConstants.IHM_LOGIN_REDIRECT_URI_LOCAL_STORAGE_KEY)
            window.localStorage.removeItem(
                IhmLoginServiceClientConstants.IHM_LOGIN_STATE_LOCAL_STORAGE_KEY)
            return response.data
        }).catch((response) => {
            console.log('ATBase: UnSuccessful call to /token endpoint')
            retrieveAuthTokenLatency.stop()
            retrieveAuthTokenError.count(1)
            retrieveAuthTokenSuccess.count(0)
            const exception = IhmLoginServiceClient.parseRetrieveAuthTokenError(response)
            return Promise.reject(exception)
        })
    }

    /** Calls /userDetail api in IhmLogin to retrieve user details based on the ihmlogin token.
     *
     * @return {*} A promise with user details on successful call.
     */
    public static retrieveUserDetails () {
        const ihmLoginVip = IhmLoginServiceClient.getIhmLoginVip()
        console.log('ATBase: Got IhmLogin Vip')

        const retrieveUserDetailsLatency = new LatencyMetric(undefined, 'RetrieveUserDetailsLatency', 'IhmFlyingDutchman', 'RetrieveUserDetails')
        const retrieveUserDetailsSuccess = new CountMetric(undefined, 'RetrieveUserDetailsSuccess', 'IhmFlyingDutchman', 'RetrieveUserDetails')
        const retrieveUserDetailsError = new CountMetric(undefined, 'RetrieveUserDetailsFailure', 'IhmFlyingDutchman', 'RetrieveUserDetails')
        retrieveUserDetailsLatency.start()

        return IhmLoginTokenProvider.getTokenData()
            .then((tokenData) => {
                if (tokenData.accessToken === undefined) {
                    throw new Exception({
                        message: 'IhmLogin Token is not set',
                    })
                }
                const storeId = EnvironmentDetail.getStoreId()
                const url = ihmLoginVip + '/userDetail?storeId=' + storeId + '&token=' + tokenData.accessToken
                return axios({
                    method: 'GET',
                    url,
                })
            })
            .then((response: AxiosResponse) => {
                console.log('ATBase: Successful call to /userDetail api')
                retrieveUserDetailsLatency.stop()
                retrieveUserDetailsSuccess.count(1)
                retrieveUserDetailsError.count(0)
                return response.data
            }).catch((response) => {
                retrieveUserDetailsLatency.stop()
                retrieveUserDetailsError.count(1)
                retrieveUserDetailsSuccess.count(0)
                return Promise.reject(new Exception({
                    message: 'Error in retrieving User details',
                }))
            })
    }

    /**
     * Parses the error response from the /token endpoint and returns the appropriate error
     */
    public static parseRetrieveAuthTokenError (response: AxiosResponse): Exception {
        console.log('ATBase: error from IhmLogin', response)
        const status = response.status
        const data = response.data

        // Usually a networking error
        if (status === 0) {
            return new IhmLoginTokenRetrievalNetworkingException({
                message: 'Error retrieving the token. Most likely a networking error',
            })
        }

        // Something wrong with our request. Could be that the auth code expired.
        if (status >= 400 && status < 500) {
            if (data && data.error && data.error_description) {
                return new IhmLoginTokenRetrievalClientErrorException({
                    message: data.error + ' ' + data.error_description,
                })
            }
        }

        if (status >= 500) {
            return new IhmLoginTokenRetrievalInternalServerErrorException({
                message: 'Server error: ' + status,
            })
        }

        return new IhmLoginTokenRetrievalUnknownErrorException({
            message: 'Unknown error: ' + status,
        })
    }

    /**
     * This method will return 'openid' as scope if no store family is found.
     * If store family is found store family will be an additional scope in the string separated by space.
     * Eg: "openid" or "openid wfm" (for when a WFM employee is signing in)
     * NOTE: Order of scopes doesn't matter.
     */
    public static generateScope (storeFamily: string): string {
        let scope = 'openid'
        if (storeFamily) {
            scope += '%20' + storeFamily
        } else {
            console.log('ATBase: Did not find store family. Not expanding scope.')
        }
        console.log('ATBase: IhmLogin scope: ', scope)
        return scope
    }

    /**
     * This method will construct and return the IHM logout URL which should be navigated to by the browser.
     * Once the logout is done, the webview should be redirected back to the IHM login page.
     * @param {boolean} rememberUsername boolean value for `keep_username` query parameter.
     * @return {string} IHM logout url string.
     */
    public static getLogoutUrl (rememberUsername: any): string {
        let redirectUri
        const signInUrl = IhmLoginServiceClient.getIhmLoginSignInUrlForAuthFlow()

        // For Dorothy, if the the user is logging out we redirect back to /user-selection-page
        if ((isConfigured(store.getState()) && getStoreInfo(store.getState()).isDorothyEnabled) || (!!DorothyUtils.getDorothyStoreInfo(EnvironmentDetail.getStoreId()) && !UserTypeService.getUserType())) {
            redirectUri = encodeURIComponent(IhmLoginServiceClient.getAssociateToolsBaseUrl() + '?') +
                IhmLoginServiceClient.getUriParamsWithEnvironmentDetails() + '%26badgeId=1234%26auth=ihm-login%23user-type-selection'
        } else {
            redirectUri = encodeURIComponent(signInUrl)
        }

        return [
            IhmLoginServiceClient.getIhmLoginVip(),
            '/logout?keep_username=' + !!rememberUsername,
            '&redirect_uri=' + redirectUri,
        ].join('')
    }

    public static logout (rememberUsername: any): Promise<void> {
        console.log('ATBase: ihmLoginSeawardServiceModule logout()')

        rememberUsername = _.isBoolean(rememberUsername) ? rememberUsername : true
        const logoutUrl = IhmLoginServiceClient.getLogoutUrl(rememberUsername)
        console.log('ATBase: ihmLoginSeawardServiceModule navigating to /logout with rememberUsername as', rememberUsername, 'and logoutUrl', logoutUrl)
        window.top.location.assign(logoutUrl)

        return Promise.resolve()
    }
}
